import { CellClassParams, CellStyle } from '@ag-grid-community/core'
import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms'
import { checkIsNoteValid } from '@helios/validators'
import { ErrorMessage } from '@northfork/ng-basics'
import { BaseRequest, RequestStatus } from './approvals.model'

/**
 *  updateNotesValidator
 */
export function updateNotesValidator(
  isHighRisk: boolean,
  notesFormControl: FormControl,
) {
  notesFormControl.markAsTouched()
  const currValidator = approvalNotesValidator(isHighRisk)
  notesFormControl.setValidators(currValidator)
  notesFormControl.updateValueAndValidity()
  return notesFormControl
}

/**
 * Validates the approval notes based on the given high risk error flag.
 *
 * @param isHighRiskErr - Indicates if the approval request has high risk errors.
 * @returns A function that validates the approval notes.
 */
export function approvalNotesValidator(isHighRiskErr = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isHighRiskErr) {
      const val: string = control?.value
      const isNoteValid = checkIsNoteValid(val)
      if (isNoteValid) {
        return null
      }
      return {
        RequestNotesValidator: {},
      }
    }
    return null
  }
}

/**
 *  check if valid row to approve
 */
export function isValidRowToApprove(
  canApproveSelectedActionType: boolean,
  approvalRequest: BaseRequest[],
  userId: number,
) {
  const approveBtnErrorMessage: Set<string> = new Set<string>()
  if (approvalRequest.length > 0) {
    if (!canApproveSelectedActionType) {
      approveBtnErrorMessage.add(
        'You do not have permissions to approve requests',
      )
    }
    if (approvalRequest.some(req => req.requesterUserId == userId)) {
      approveBtnErrorMessage.add('You cannot approve your own requests')
    }
    if (approvalRequest.some(req => req.status != 'PENDING')) {
      approveBtnErrorMessage.add('Please Select a Pending Request')
    }
  }
  return approveBtnErrorMessage
}

/**
 *  check if valid row to deny
 */
export function isValidRowToDeny(
  canApproveSelectedActionType: boolean,
  approvalRequest: BaseRequest[],
  userId: number,
) {
  const denyBtnErrorMessage: Set<string> = new Set<string>()
  if (approvalRequest.length > 0) {
    if (!canApproveSelectedActionType) {
      if (approvalRequest.some(req => req.requesterUserId != userId)) {
        denyBtnErrorMessage.add('You can deny only your request')
      }
    }
    if (approvalRequest.some(req => req.status != 'PENDING')) {
      denyBtnErrorMessage.add('Please Select a Pending Request')
    }
  }
  return denyBtnErrorMessage
}

export const requestNotesValidationMessages: ErrorMessage[] = [
  {
    error: 'RequestNotesValidator',
    format: (label, error): string => {
      return `Please provide justification for Approval (3 words minimum)`
    },
  },
]

/**
 * Determines the cell style for a given request status.
 *
 * @param  params - The parameters for the function
 * @returns - The cell style for the request status
 */
export function getCellStyleForRequestStatus(
  params: CellClassParams,
): CellStyle {
  {
    if (params.value === RequestStatus.PENDING)
      return { backgroundColor: '#FFEB9C' }
    else if (params.value === RequestStatus.APPROVED)
      return { backgroundColor: '#C6EFCE' }
    else if (params.value === RequestStatus.DENIED)
      return { backgroundColor: '#f8ced2' }
    else return { 'background-color': '#00000000' }
  }
}

/**
 * evaluateErrorCount
 */
export function evaluateErrorCount(count: number): string {
  if (count < 0) {
    return 'NA'
  }
  return count.toString()
}
